import { dpTypeForDataSource, getIdInfoFromId, getParentIdForId, IdInfo } from 'src/components/utils/arnUtil';
import {
  DataPermissionList,
  FGAPList,
  ListFineGrainAccessPolicyRequest,
  ListFineGrainAccessPolicyResult,
  Resource,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import {
  DATAZONE_TYPE,
  FGA_POLICY_TYPE_PREFIX,
  GALAXI_TYPE,
  RESOURCE_ITEM,
  STATUS_ACTIVE,
  TAG_ITEM,
} from 'src/commons/constants';
import { listFgaPolicies } from 'src/api/permissions';
import { isValidPrincipalNotRoot } from 'src/commons/validationUtils';
import { RESOURCE_TYPE_TABLE } from 'src/components/permissions/myBaselining/baseliningUtils';

export const fetchFineGrainPoliciesOnDataset = async (idInfo: IdInfo): Promise<FGAPList> => {
  let fgaPolicyList: FGAPList = [];
  try {
    const baseRequest: ListFineGrainAccessPolicyRequest = {
      status: STATUS_ACTIVE,
      tableData: {
        tableCatalogId: idInfo.catalogId,
        clusterId: idInfo.clusterId,
        bucketName: idInfo.bucketName,
        databaseName: idInfo.databaseName,
        schemaName: idInfo.schemaName,
        tableName: idInfo.tableName,
        region: idInfo.region,
      },
    };
    let listFgaPoliciesForResourceResult: ListFineGrainAccessPolicyResult = await listFgaPolicies({ ...baseRequest });
    fgaPolicyList.push(...listFgaPoliciesForResourceResult.FineGrainAccess);
    while (listFgaPoliciesForResourceResult.nextToken) {
      listFgaPoliciesForResourceResult = await listFgaPolicies({
        ...baseRequest,
        nextToken: listFgaPoliciesForResourceResult.nextToken,
      });
      fgaPolicyList.push(...listFgaPoliciesForResourceResult.FineGrainAccess);
    }
    return fgaPolicyList;
  } catch (err) {
    console.log('Failed to fetch Fine grain policies on dataset.' + err.message);
    return [];
  }
};

export const resourceItemForConsumerTable = (id: string): DataCatalogResource => {
  const idInfo: IdInfo = getIdInfoFromId(id);
  const dataSourceId = idInfo.dataSource;
  return {
    idInfo,
    accountId: idInfo.catalogId,
    region: idInfo.region,
    type: RESOURCE_TYPE_TABLE,
    dataCatalogObjectDetails: {
      dataSourceId: dataSourceId,
      clusterIdentifier: idInfo.clusterId,
      bucketName: idInfo.bucketName,
      databaseName: idInfo.databaseName,
      schemaName: idInfo.schemaName,
      tableName: idInfo.tableName,
    },
    dpType: dpTypeForDataSource(dataSourceId),
    tagResourceId: id,
    tagUpperLevelResourceId: getParentIdForId(id),
  };
};

export interface DataCatalogResource {
  idInfo?: IdInfo;
  accountId: string;
  region: string;
  type: string;
  dataCatalogObjectDetails?: DataCatalogObjectDetails;
  dpType: string;
  tagResourceId?: string;
  tagUpperLevelResourceId?: string;
  tagDetails?: {
    tagKey: string;
    tagValue: string;
  };
  [key: string]: any;
}

export interface DataCatalogObjectDetails {
  dataSourceId: string;
  clusterIdentifier?: string;
  databaseName: string;
  schemaName?: string;
  tableName?: string;
  fgapName?: string;
  redshiftWorkgroupName?: string;
}

export const getHumanReadableResourceString = (resource: Resource): string => {
  if (!resource) {
    return 'Resource';
  }
  if (resource.redshiftTagPolicy) {
    const item = resource.redshiftTagPolicy;
    return `${item.catalogId}.${item.tagKey}.${item.tagValue}`;
  }
  if (resource.lFTagPolicy) {
    const item = resource.lFTagPolicy;
    return `${item.catalogId}.${item.expression?.[0]?.tagKey}.${item.expression?.[0]?.tagValues?.[0]}`;
  }
  if (resource.dataCellsFilter) {
    const item = resource.dataCellsFilter;
    return `${item.tableCatalogId}.${item.databaseName}.${item.tableName}.${item.name}`;
  }
  if (resource.table) {
    const item = resource.table;
    return item.name ? `${item.catalogId}.${item.databaseName}.${item.name}` : `${item.catalogId}.${item.databaseName}`;
  }
  if (resource.database) {
    const item = resource.database;
    return `${item.catalogId}.${item.name}`;
  }
  return 'Resource';
};

export interface PermissionDropdownOption {
  label: string;
  id: string;
  type: string;
  value: string;

  description?: string;

  datasetId?: string;
  catalogId?: string;
  catalogName?: string;
  clusterId?: string;
  redshiftWorkgroupName?: string;
  bucketName?: string;
  databaseName?: string;
  schemaName?: string;
  tableName?: string; // TODO: combine these two
  datasetName?: string;

  ownerId?: string; // TODO: combine these two
  owner?: string;
  permissionId?: string;
  workspaceAccess?: boolean;
  principals?: string[];
  publisherRole?: string;

  // FGAP
  fgapName?: string;

  // DataZone
  dataZoneAssetId?: string;
  dataZoneConsumerProjectId?: string;

  // Galaxi
  teamName?: string;
  iamRole?: string;
  groupEmail?: string;
  accountAssociations?: Array<{ accountId?: string; iamRole?: string }>;

  // Tags
  tagKey?: string;
  tagValue?: string;
}

export const createPermissionDropdownItemForFgap = (
  fgaPolicy: any,
  catalogName: string,
  consumedPermission: DataPermissionList,
  props: any,
) => {
  return {
    label: `Request access to ` + `"${fgaPolicy.tableData.physicalName}"`,
    description: fgaPolicy.description,
    id: fgaPolicy.id,
    fgapName: fgaPolicy.tableData.physicalName,
    type: FGA_POLICY_TYPE_PREFIX,
    value: fgaPolicy.id,
    catalogId: fgaPolicy.tableData.tableCatalogId,
    clusterId: fgaPolicy.tableData.clusterId,
    bucketName: fgaPolicy.tableData.bucketName,
    databaseName: fgaPolicy.tableData.databaseName,
    schemaName: fgaPolicy.tableData.schemaName,
    datasetName: fgaPolicy.tableData.tableName,
    tableName: fgaPolicy.tableData.tableName,
    ownerId: fgaPolicy.ownerId,
    catalogName: catalogName,
    permissionId: fgaPolicy.dataPermissionId,
    workspaceAccess:
      consumedPermission.filter((consumedPermission) =>
        props.activeWorkspace.workspaceId.includes(consumedPermission.dataLakePrincipal),
      ).length !== 0,
    principals: consumedPermission
      .map((consumedPermission) => consumedPermission.dataLakePrincipal)
      .filter((dataLakePrincipal) => isValidPrincipalNotRoot(dataLakePrincipal)),
  } as PermissionDropdownOption;
};

export const createPermissionDropdownItemForSingleTable = (
  permissionData,
  resourceId: string,
  idInfo: IdInfo,
  catalogName: string,
  dataset?: any,
) => {
  let datasetPermissionId = permissionData?.permissionId;
  let isSingleDatasetConsumed = permissionData?.accountConsumed;
  let publisherRole = permissionData?.publisherRole;
  return {
    label: `Request access to ` + `"${idInfo.tableName}"`,
    id: resourceId,
    value: resourceId,
    type: RESOURCE_ITEM,
    datasetId: dataset?.Id,
    datasetName: idInfo.tableName,
    tableName: idInfo.tableName,
    databaseName: idInfo.databaseName,
    owner: dataset?.PrimaryOwner,
    catalogId: idInfo.catalogId,
    bucketName: idInfo.bucketName,
    catalogName: catalogName,
    permissionId: datasetPermissionId,
    workspaceAccess: isSingleDatasetConsumed,
    publisherRole: publisherRole,
    principals: permissionData?.iamPrincipals,
  } as PermissionDropdownOption;
};

export const createPermissionDropdownItemForDataZone = (
  props: any,
  idInfo: IdInfo,
  catalogName: string,
  dataset?: any,
) => {
  return {
    label: `Request DataZone access`,
    value: dataset.DataZoneConfig.AssetId,
    type: DATAZONE_TYPE,
    id: dataset.Id,
    dataZoneAssetId: dataset.DataZoneConfig.AssetId,
    // dataZoneConsumerProjectId will be set in accessSelectModal
    publisherRole: (props.activeWorkspace.workspaceRoleArn as string).replace(
      props.activeWorkspace.accountId,
      idInfo.catalogId,
    ),
    catalogId: idInfo.catalogId,
    datasetName: idInfo.tableName,
    databaseName: idInfo.databaseName,
    clusterId: idInfo.clusterId,
    redshiftWorkgroupName: idInfo.redshiftWorkgroupName,
    schemaName: idInfo.schemaName,
    region: idInfo.region,
    owner: dataset.PrimaryOwner,
    catalogName: catalogName,
  } as PermissionDropdownOption;
};

export const createPermissionDropdownItemForTag = (
  tag: any,
  catalogName: string,
  dataset: any,
  permissionData: any,
) => {
  let tagId = `${tag.tagKey}.${tag.tagValue}.${tag.catalogId}`;
  let tagPermissionId = permissionData?.permissionId;
  let consumed = permissionData?.consumed;
  return {
    label: `Request ${tag.tagKey}.${tag.tagValue} access`,
    id: tagId,
    type: TAG_ITEM,
    value: tagId,
    tagKey: tag.tagKey,
    tagValue: tag.tagValue,
    catalogId: tag.catalogId,
    owner: dataset.Owners[0],
    catalogName: catalogName,
    permissionId: tagPermissionId,
    workspaceAccess: consumed,
    principals: permissionData.iamPrincipals,
  } as PermissionDropdownOption;
};

export const createPermissionDropdownItemForGalaxi = (galaxiGroup: any) => {
  return {
    label: galaxiGroup?.groupName,
    value: galaxiGroup?.groupName,
    type: GALAXI_TYPE,
    id: galaxiGroup?.groupName,
    teamName: galaxiGroup?.teamName,
    iamRole: galaxiGroup?.iamRole,
    groupEmail: galaxiGroup?.groupEmail,
    accountAssociations: galaxiGroup?.accountAssociations ?? [],
  } as PermissionDropdownOption;
};
